import { isScanPositionView } from '../../helpers/viewMedia.ts';
import { defaultContentLayers, KNOWLEDGE_VIEW_CONTENT_LAYERS_VERSION } from '../../Interfaces/ViewerInterfaces.ts';

export type KnowledgeBookFirstView = '3d' | 'pano';

export interface NewKnowledgeBookCreationInput {
    name: string;
    description: string;
    classificationTags: string[];
    ontologyId: string;
    firstView: KnowledgeBookFirstView;
    scanPositionId?: string;
}

export interface NewKnowledgeBookCreationResult {
    knowledgeBook: IKnowledgeBook;
    initialView?: IKnowledgeView;
    initialViewError?: unknown;
}

export function isNewKnowledgeBookCreationInputValid(
    input: NewKnowledgeBookCreationInput,
    panoAvailable: boolean,
    hasSelectedScanPosition: boolean,
): boolean {
    if (!input.name.trim() || !input.ontologyId || input.classificationTags.length === 0) {
        return false;
    }

    if (input.firstView === 'pano') {
        return panoAvailable && hasSelectedScanPosition && !!input.scanPositionId;
    }

    return input.firstView === '3d';
}

function getOntologyScope(facility: IFacilityDetail, ontologyId: string): Set<string> {
    const parts = facility?.OntologyParts ?? [];
    const partMap = new Map(parts.map(part => [part.ID, part]));
    const selected = partMap.get(ontologyId);
    const scope = new Set<string>();

    const ancestors = new Set<string>();
    let current = selected;
    while (current && !ancestors.has(current.ID)) {
        ancestors.add(current.ID);
        scope.add(current.ID);
        current = current.OntologyParentID ? partMap.get(current.OntologyParentID) : undefined;
    }

    const descendants = new Set<string>();
    const walkDescendants = (part: IOntologyPart | undefined) => {
        if (!part || descendants.has(part.ID)) return;
        descendants.add(part.ID);
        scope.add(part.ID);
        parts.forEach(child => {
            if (child.OntologyParentID === part.ID) {
                walkDescendants(child);
            }
        });
    };

    walkDescendants(selected);
    return scope;
}

/**
 * Matches FacilityStore.getSelectedOntologyPartViews without depending on the
 * selected-ontology store state. This keeps the creation dialog's map scope
 * deterministic while preserving inherited and descendant content behavior.
 */
export function getOntologyScopedScanPositions(
    facility: IFacilityDetail | null | undefined,
    ontologyId: string | undefined,
): IView360Detail[] {
    if (!facility || !ontologyId) return [];

    const scope = getOntologyScope(facility, ontologyId);
    return (facility.Views ?? []).filter(view =>
        !!view?.OntologyID && scope.has(view.OntologyID) && isScanPositionView(view)
    );
}

export function isPanoFirstViewAvailable(
    ontology: IOntologyPart | null | undefined,
    scanPositions: IView360Detail[],
): boolean {
    return !!ontology?.Map?.MapFileImageUrl && scanPositions.length > 0;
}

export function getDefaultKnowledgeViewDisplayProperties(firstView: KnowledgeBookFirstView): IDisplayProperties {
    const layers = Object.keys(defaultContentLayers).filter(key =>
        defaultContentLayers[key as keyof typeof defaultContentLayers]
    );

    if (firstView === 'pano' && !layers.includes('positions360')) {
        layers.push('positions360');
    }

    return {
        ContentLayers: layers,
        ContentLayersVersion: KNOWLEDGE_VIEW_CONTENT_LAYERS_VERSION,
    };
}

export function buildInitialKnowledgeViewDraft(
    input: NewKnowledgeBookCreationInput,
    knowledgeBook: IKnowledgeBook,
    viewName: string,
): IKnowledgeViewDetail {
    return {
        ID: null,
        VersionToken: null,
        Name: viewName,
        Description: '',
        KnowledgeBookID: knowledgeBook.ID,
        OntologyID: input.firstView === '3d' ? input.ontologyId : null,
        Type: input.firstView === '3d' ? 2 : 1,
        ViewID: input.firstView === 'pano' ? input.scanPositionId : null,
        KVImage: '',
        Tags: [],
        DisplayProperties: getDefaultKnowledgeViewDisplayProperties(input.firstView),
        View: null,
    };
}
